import React from "react";
import PropTypes from "prop-types";

import StatusSelect from "modules/appointments/components/StatusSelect";
import {
  STATUS_LABEL,
  STATUS_TONE,
  END_STATUSES,
} from "modules/appointments/helper/appointmentConstants";
import { formatTimeSlot } from "modules/appointments/helper/formatters";
import { formatLongDate } from "modules/patient/helper/patientFormatters";
import AppointmentDocumentsMenu from "modules/patient/components/AppointmentDocumentsMenu";

import MOBILE from "../mobileConfig";

/** Figma 5330:2 — the calendar mark before the date. */
export const CalendarIcon = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="2" y="3.2" width="12" height="11" rx="2" />
    <path d="M2 6.6h12M5.4 1.8v2.6M10.6 1.8v2.6" />
  </svg>
);

/**
 * Row 2 — when this appointment is, what state it is in, and a way into the
 * rest of its detail.
 *
 * The date and the chevron open the detail sheet; the status control sits
 * between them and opens its own menu. They are SIBLINGS, not nested: making
 * the whole strip one button would have put a dropdown inside a button, so
 * every attempt to change status would also have opened the sheet behind it.
 * The appointments dashboard reached the same conclusion about its rows
 * (AppointmentsDashboard.js:602-605).
 *
 * The chevron sits AFTER the status, per the board — far right, where a
 * disclosure belongs, rather than tucked against the date.
 */
const MobileApptStrip = ({
  appointment,
  updating,
  onChangeStatus,
  onOpenDetails,
  readOnly,
  reasonName,
}) => {
  if (!appointment) return null;

  /* The year is kept, where the board's example drops it: an appointment in
     another year is exactly the case where the date alone misleads. */
  const when = [
    formatLongDate(appointment.appointmentDate),
    formatTimeSlot(appointment.timeSlot, appointment.serviceDuration),
  ]
    .filter(Boolean)
    .join(" · ");

  /* Reason and the documents button, on the strip itself (MOBILE.apptStripDetail).
     Both are still in the detail sheet: this is the same reading and the same
     control, lifted to where the board (5330:2) draws them, not moved out of
     it. Only where there IS a sheet to mirror — Create Rx's read-only strip
     has no detail behind it and no reason to show. */
  const reason =
    [reasonName, appointment.reasonDetails].filter(Boolean).join(" — ") || "—";
  const detail = MOBILE.apptStripDetail && !!onOpenDetails;

  return (
    <>
    <div className={`zym-appt${detail ? " has-detail" : ""}`}>
      {onOpenDetails ? (
        <button
          type="button"
          className="zym-appt-when"
          onClick={onOpenDetails}
          aria-label="Appointment details"
        >
          <CalendarIcon />
          <span className="zym-appt-when-text">{when || "Appointment"}</span>
        </button>
      ) : (
        <span className="zym-appt-when is-static">
          <CalendarIcon />
          <span className="zym-appt-when-text">{when || "Appointment"}</span>
        </span>
      )}

      <div className="zym-appt-status">
        <StatusSelect
          value={appointment.status}
          tone={STATUS_TONE[appointment.status] || "progress"}
          label={STATUS_LABEL[appointment.status] || "—"}
          labels={STATUS_LABEL}
          tones={STATUS_TONE}
          disabled={updating}
          /* Terminal statuses are a dead end server-side, and a caller can ask
             for a plain pill too — see the `readOnly` prop. */
          readOnly={readOnly || END_STATUSES.indexOf(appointment.status) !== -1}
          onChange={onChangeStatus}
        />
      </div>

      {onOpenDetails && (
      <button
        type="button"
        className="zym-appt-more"
        onClick={onOpenDetails}
        aria-label="Appointment details"
      >
        <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <path
            d="M4 2l4 4-4 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      )}
    </div>

    {/* A SIBLING of the line above, not inside it: that line is a flex row of
        three controls, and the detail belongs under all three. Its own
        buttons, so a tap on "View all" cannot reach the date button's handler
        and open the sheet instead. */}
    {detail && (
      <div className="zym-appt-detail">
        <div className="zym-af zym-appt-detail-reason">
          <span className="zym-af-label">Reason</span>
          <div className="zym-af-value">{reason}</div>
        </div>
        <div className="zym-af zym-appt-detail-docs">
          <span className="zym-af-label">Appointment documents</span>
          <div className="zym-af-value">
            <AppointmentDocumentsMenu appointmentId={appointment.id} />
          </div>
        </div>
      </div>
    )}
    </>
  );
};

MobileApptStrip.propTypes = {
  appointment: PropTypes.object,
  updating: PropTypes.bool,
  onChangeStatus: PropTypes.func.isRequired,
  onOpenDetails: PropTypes.func,
  /** Render the status as a plain pill with no dropdown. */
  readOnly: PropTypes.bool,
  /** The reason's name from the master list; `reasonDetails` rides on the
      appointment. Shown under the strip beside the documents button. */
  reasonName: PropTypes.string,
};

export default MobileApptStrip;
